import { useState } from 'react';
import { useParams } from 'react-router-dom';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  useGetDemandReviewWorkspaceQuery,
  useListDemandReviewCitationsQuery,
  useMatchCitationsMutation,
  useStartWorkflowMutation,
  useVerifyAllCitationsMutation,
} from '@/services/api/demandReviewService';
import { DemandReviewWorkspaceStatus } from '@/services/types/demand-review-types';

import { CitationList } from './CitationList';
import { CitationViewer } from './CitationViewer';
import type { DemandCitationHighlight } from './DemandPdfViewer';
import { DemandPdfViewer } from './DemandPdfViewer';
import { DemandUploader } from './DemandUploader';
import { ExhibitManager } from './ExhibitManager';
import { ProofreadingResults } from './ProofreadingResults';

interface DemandReviewWorkspaceProps {
  matterId?: string;
  workspaceId?: string;
}

export const DemandReviewWorkspace = ({
  matterId: propMatterId,
  workspaceId: propWorkspaceId,
}: DemandReviewWorkspaceProps) => {
  const params = useParams<{ matterId: string; workspaceId: string }>();
  const matterId = propMatterId ?? params.matterId ?? '';
  const workspaceId = propWorkspaceId ?? params.workspaceId ?? '';

  const [selectedCitationId, setSelectedCitationId] = useState<string | null>(
    null,
  );
  const [activeTab, setActiveTab] = useState('citations');
  const [demandPageToNavigate, setDemandPageToNavigate] = useState<
    number | null
  >(null);
  // Citation highlight from proofreading findings (for demand document)
  const [demandCitationToHighlight, setDemandCitationToHighlight] =
    useState<DemandCitationHighlight | null>(null);

  const { data: workspace, isLoading: isLoadingWorkspace } =
    useGetDemandReviewWorkspaceQuery(
      { matterId, workspaceId, includeMarkdown: true },
      { skip: !matterId || !workspaceId },
    );

  const { data: citations = [], isLoading: isLoadingCitations } =
    useListDemandReviewCitationsQuery(
      { matterId, workspaceId },
      { skip: !matterId || !workspaceId },
    );

  const [startWorkflow, { isLoading: isStartingWorkflow }] =
    useStartWorkflowMutation();
  const [matchCitations, { isLoading: isMatchingCitations }] =
    useMatchCitationsMutation();
  const [verifyAllCitations, { isLoading: isVerifyingAll }] =
    useVerifyAllCitationsMutation();

  const selectedCitation = selectedCitationId
    ? citations.find((c) => c.id === selectedCitationId)
    : null;

  const handleStartWorkflow = async () => {
    if (!matterId || !workspaceId) return;
    await startWorkflow({ matterId, workspaceId });
  };

  const handleMatchCitations = async () => {
    if (!matterId || !workspaceId) return;
    await matchCitations({ matterId, workspaceId });
  };

  const handleVerifyAll = async () => {
    if (!matterId || !workspaceId) return;
    await verifyAllCitations({ matterId, workspaceId });
  };

  const getStatusBadgeColor = (status: DemandReviewWorkspaceStatus) => {
    switch (status) {
      case DemandReviewWorkspaceStatus.COMPLETED:
        return 'bg-green-100 text-green-800';
      case DemandReviewWorkspaceStatus.FAILED:
        return 'bg-red-100 text-red-800';
      case DemandReviewWorkspaceStatus.VERIFYING:
      case DemandReviewWorkspaceStatus.TRANSFORMING:
      case DemandReviewWorkspaceStatus.PARSING_CITATIONS:
        return 'bg-blue-100 text-blue-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  if (isLoadingWorkspace) {
    return (
      <div className="flex h-96 items-center justify-center">
        <div className="animate-pulse text-muted-foreground">
          Loading workspace...
        </div>
      </div>
    );
  }

  if (!workspace) {
    return (
      <div className="flex h-96 items-center justify-center">
        <div className="text-muted-foreground">Workspace not found</div>
      </div>
    );
  }

  const showUploader =
    workspace.status === DemandReviewWorkspaceStatus.CREATED &&
    !workspace.demand_blob_id;

  const showStartWorkflow =
    workspace.demand_blob_id &&
    workspace.status === DemandReviewWorkspaceStatus.CREATED;

  const isProcessing = [
    DemandReviewWorkspaceStatus.TRANSFORMING,
    DemandReviewWorkspaceStatus.PARSING_CITATIONS,
    DemandReviewWorkspaceStatus.MATCHING_CITATIONS,
    DemandReviewWorkspaceStatus.VERIFYING,
  ].includes(workspace.status);

  return (
    <div className="flex h-full flex-col gap-4 p-4">
      {/* Header */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <CardTitle>{workspace.name}</CardTitle>
              <Badge className={getStatusBadgeColor(workspace.status)}>
                {workspace.status.replace(/_/g, ' ')}
              </Badge>
            </div>
            <div className="flex items-center gap-2">
              {showStartWorkflow && (
                <Button
                  onClick={handleStartWorkflow}
                  disabled={isStartingWorkflow}
                >
                  {isStartingWorkflow ? 'Starting...' : 'Start Processing'}
                </Button>
              )}
              {workspace.status ===
                DemandReviewWorkspaceStatus.READY_FOR_EXHIBITS && (
                <Button
                  onClick={handleMatchCitations}
                  disabled={isMatchingCitations}
                >
                  {isMatchingCitations ? 'Matching...' : 'Match Citations'}
                </Button>
              )}
              {(workspace.status ===
                DemandReviewWorkspaceStatus.READY_FOR_VERIFICATION ||
                workspace.status === DemandReviewWorkspaceStatus.COMPLETED) &&
                workspace.pending_verification_count > 0 && (
                  <Button onClick={handleVerifyAll} disabled={isVerifyingAll}>
                    {isVerifyingAll
                      ? 'Verifying...'
                      : `Verify All (${workspace.pending_verification_count})`}
                  </Button>
                )}
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-4 gap-4 text-sm">
            <div>
              <div className="text-muted-foreground">Citations</div>
              <div className="text-2xl font-semibold">
                {workspace.citation_count}
              </div>
            </div>
            <div>
              <div className="text-muted-foreground">Exhibits</div>
              <div className="text-2xl font-semibold">
                {workspace.exhibit_count}
              </div>
            </div>
            <div>
              <div className="text-muted-foreground">Verified</div>
              <div className="text-2xl font-semibold">
                {workspace.verified_count}
              </div>
            </div>
            <div>
              <div className="text-muted-foreground">Pending</div>
              <div className="text-2xl font-semibold">
                {workspace.pending_verification_count}
              </div>
            </div>
          </div>
          {workspace.error_message && (
            <div className="mt-4 rounded-md bg-red-50 p-3 text-sm text-red-700">
              {workspace.error_message}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Upload Section */}
      {showUploader && (
        <DemandUploader matterId={matterId} workspaceId={workspaceId} />
      )}

      {/* Processing Indicator */}
      {isProcessing && (
        <Card>
          <CardContent className="py-8 text-center">
            <div className="animate-pulse text-muted-foreground">
              Processing demand... This may take a few minutes.
            </div>
          </CardContent>
        </Card>
      )}

      {/* Main Content */}
      {!showUploader && !isProcessing && (
        <div className="flex flex-1 min-h-0 gap-4">
          {/* Left Panel - Citations, Exhibits & Proofreading */}
          <div className="flex w-1/3 min-h-0 flex-col">
            <Tabs
              value={activeTab}
              onValueChange={(tab) => {
                setActiveTab(tab);
                // Clear selected citation when switching to exhibits or proofreading
                // so the PDF viewer is shown instead
                if (tab === 'exhibits' || tab === 'proofreading') {
                  setSelectedCitationId(null);
                }
              }}
              className="flex flex-1 min-h-0 flex-col"
            >
              <TabsList className="grid w-full grid-cols-3 flex-shrink-0">
                <TabsTrigger value="citations">
                  Citations ({workspace.citation_count})
                </TabsTrigger>
                <TabsTrigger value="exhibits">
                  Exhibits ({workspace.exhibit_count})
                </TabsTrigger>
                <TabsTrigger value="proofreading">Proofreading</TabsTrigger>
              </TabsList>
              <TabsContent value="citations" className="mt-4 flex-1 min-h-0 overflow-y-auto">
                <CitationList
                  citations={citations}
                  isLoading={isLoadingCitations}
                  selectedCitationId={selectedCitationId}
                  onSelectCitation={setSelectedCitationId}
                  matterId={matterId}
                  workspaceId={workspaceId}
                />
              </TabsContent>
              <TabsContent value="exhibits" className="mt-4 flex-1 min-h-0 overflow-y-auto">
                <ExhibitManager matterId={matterId} workspaceId={workspaceId} />
              </TabsContent>
              <TabsContent value="proofreading" className="mt-4 flex-1 min-h-0 overflow-y-auto">
                <ProofreadingResults
                  matterId={matterId}
                  workspaceId={workspaceId}
                  demandArtifactId={workspace?.demand_artifact_id}
                  onDemandCitationClick={(citation) => {
                    // Clear any selected citation to show the PDF viewer
                    setSelectedCitationId(null);
                    // Set the citation to highlight in the demand viewer
                    setDemandCitationToHighlight(citation);
                  }}
                />
              </TabsContent>
            </Tabs>
          </div>

          {/* Right Panel - PDF Viewer or Citation Viewer */}
          <div className="flex-1">
            {(() => {
              // Show citation viewer if a citation is selected
              if (selectedCitation) {
                return (
                  <CitationViewer
                    citation={selectedCitation}
                    demandMarkdown={workspace.demand_markdown}
                  />
                );
              }

              // Show PDF viewer if document is available
              if (workspace.demand_pdf_blob_id || workspace.demand_blob_id) {
                return (
                  <DemandPdfViewer
                    matterId={matterId}
                    workspaceId={workspaceId}
                    pageToNavigate={demandPageToNavigate}
                    citationToHighlight={demandCitationToHighlight}
                    onPageNavigated={() => setDemandPageToNavigate(null)}
                    onCitationHighlighted={() =>
                      setDemandCitationToHighlight(null)
                    }
                  />
                );
              }

              // Show placeholder if no document
              const statusMessage =
                workspace.status === DemandReviewWorkspaceStatus.TRANSFORMING
                  ? 'Converting document to PDF...'
                  : 'No demand document uploaded';

              return (
                <Card className="flex h-full flex-col">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm">Demand Document</CardTitle>
                  </CardHeader>
                  <CardContent className="flex-1 overflow-hidden p-0">
                    <div className="flex h-full items-center justify-center text-muted-foreground">
                      {statusMessage}
                    </div>
                  </CardContent>
                </Card>
              );
            })()}
          </div>
        </div>
      )}
    </div>
  );
};
